import { Injectable } from "@angular/core";
import { Observable, BehaviorSubject } from "rxjs";
import { loadModules } from "esri-loader";

import { GISConfig } from "../models/gis-config.model";


// Models
import { LunaDomain } from "../../builder/models/domain.model";
import { GisAlert, SystemCheckDefinition, SystemCheckType } from "../models/gis-alert.model";

// Services
import { GisTokenService } from "../../gis/services/gis-token.service";


/*
 *  THIS SERVICE MEANT TO QUERY GIS AND GENERATE ALERTS
 *  TO QUERY GIS FOR OTHER DATA (WITHOUT ALERTS) USE GisQueryService 
 */
@Injectable()
export class GisAlertService {
    config: GISConfig;
    domains = new Array<LunaDomain>();
    domainStrings: any[];
    mapServerUrls = [];
    rasterServerUrls = [];

    foundAlerts = new BehaviorSubject<Array<GisAlert>>([]);
    foundAlerts$ = this.foundAlerts.asObservable();
    addFoundAlert(gisAlert: GisAlert) {
        let temp = this.foundAlerts.getValue();
        // Only allow unique alerts
        if (!this.foundAlerts.getValue().find(x => x.systemCheck.alertName === gisAlert.systemCheck.alertName)) {
            temp.push(gisAlert);
            temp.sort((a, b) => (a.systemCheck.alertName > b.systemCheck.alertName) ? 1 : (b.systemCheck.alertName > a.systemCheck.alertName) ? -1 : 0);
            this.foundAlerts.next(temp);
        }
    }

    clearAlerts() {
        this.foundAlerts.next([]);
    }


    constructor(
        public readonly tokenService: GisTokenService
    ) {
        this.config = window["GIS.Conf"] as GISConfig;
        if (this.config) {
            this.mapServerUrls = [
                this.config.yearlyGISDataUrl,
                this.config.biWeeklyGISDataUrl,
                this.config.dailyGISDataUrl,
                this.config.usdwAreasUrl
            ];
            this.rasterServerUrls = [this.config.glacialDriftGISUrl];
        }
    }

    async queryFeatureLayers(inputPoints: number[][], check: SystemCheckDefinition): Promise<any> {
        this.clearAlerts();
        // Query Feature layers
        for (let i = 0; i < this.mapServerUrls.length; i++) {
            this.getGISService(this.mapServerUrls[i]).then(async gisService => {
                if (gisService["data"].layers) {
                    const applicableLayers =
                        gisService["data"].layers.filter(x => x.name.toLowerCase()
                            .includes(check.alertCategory.toLowerCase()));                    
                    inputPoints.forEach(async point => {
                        // Query all sublayers
                        applicableLayers.forEach(async layer => {
                            (await this.queryLayers(this.mapServerUrls[i], layer, check, point));
                        });
                    });
                }
            });
        }
    }

    async getGISService(gisServiceUrl: string): Promise<Observable<any>> {
        var error;
        try {
            const [esriRequest] = await loadModules([
                "esri/request"
            ]);
            return this.tokenService.getToken().then(token => {
                // Request Feature Server
                return esriRequest(gisServiceUrl + "?f=json&token=" + token,
                    {
                        responseType: "json",
                        token: token
                    });
            });
        } catch (error) {
            console.log(`We have an error: ${error}`);
        }
    }

    async queryRasterLayers(inputPoints: number[][], check: SystemCheckDefinition): Promise<any> {
        // Identify (query) ImageServer with Raster image maps
        this.rasterServerUrls.forEach(rasterUrl => {
            inputPoints.forEach(async point => {
                (await this.queryImageServer(rasterUrl, check, point));
            });
        });
    }

    async queryImageServer(gisServiceUrl: string, check: SystemCheckDefinition, point: number[]): Promise<void> {
        try {
            const [ImageServiceIdentifyTask, ImageServiceIdentifyParameters, Point] = await loadModules([
                "esri/tasks/ImageServiceIdentifyTask",
                "esri/tasks/support/ImageServiceIdentifyParameters",
                "esri/geometry/Point"
            ]);

            let imageServiceIdentityTask = new ImageServiceIdentifyTask(gisServiceUrl);

            const testPoint = new Point({
                latitude: point[0],
                longitude: point[1]
            });

            let params = new ImageServiceIdentifyParameters();
            params.geometry = testPoint;
            params.returnGeometry = false;

            imageServiceIdentityTask.execute(params).then(result => {
                if (result.value) {
                    // Need to massage/create the attributes
                    let attributes = {
                        value: result["value"]
                    }
                    this.addFoundAlert(new GisAlert(gisServiceUrl, gisServiceUrl, check, attributes));
                }
            });

        } catch (error) {
            console.log(`We have an error: ${error}`);
        }
    }

    async queryLayers(gisServiceUrl: string, layer: any, check: SystemCheckDefinition, point: number[]): Promise<void> {
        var error;
        try {
            return this.tokenService.getToken().then(async token => {
                const [Query, QueryTask, Point] = await loadModules([
                    "esri/tasks/support/Query",
                    "esri/tasks/QueryTask",
                    "esri/geometry/Point"
                ]);
                let featureLayerUrl = gisServiceUrl;
                // Sub layers????
                if (layer) {
                    featureLayerUrl += "/" + layer.id;
                }
                featureLayerUrl += "?f=json&token=" + token;

                const testQueryTask = new QueryTask({
                    url: featureLayerUrl
                });
                const testPoint = new Point({
                    latitude: point[0],
                    longitude: point[1]
                });
                let query = new Query();

                if (check.buffer === 0) {
                    query.spatialRelationship = "intersects";
                    query.geometry = testPoint;
                } else {
                    query.geometry = testPoint;
                    query.spatialRelationship = "intersects";
                    query.distance = check.buffer;
                    query.units = check.bufferMeasurement;
                }
                // Check for Advanced query
                if (check.systemCheckType === SystemCheckType.Advanced) {
                    query.where = check.query;
                }
                query.returnGeometry = false;
                query.outFields = ["*"];
                testQueryTask.execute(query).then(result => {
                    if (result.features && result.features.length > 0) {
                        this.addFoundAlert(new GisAlert(featureLayerUrl,
                            layer.name,
                            check,
                            result.features[0].attributes));
                    }
                });
            });
        } catch (error) {
            console.log(`We have an error: ${error}`);
        }
    }



    toCamel(o) {
        var newO, origKey, newKey, value;
        let returnArray = [];
        for (let i = 0; i < o.length; i++) {
            newO = {}
            for (origKey in o[i]) {
                if (o[i].hasOwnProperty(origKey)) {
                    newKey = (origKey.charAt(0).toLowerCase() + origKey.slice(1) || origKey).toString();
                    value = o[i][origKey];
                    if (value instanceof Array || (value !== null && value.constructor === Object)) {
                        value = this.toCamel(value);
                    }
                    newO[newKey] = value;
                }
                //}
            }
            returnArray.push(newO);
        }
        return returnArray;
    }

    // THESE WILL NEED TO BE MOVED OUT TO INDIVIDUAL FILES
    SYSTEM_CHECK = Object.freeze({
        "All New Wells": 0,
        "Active Underground Mine Layer": 1
    });
}